import { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  useFetchLiderTramos,
  useCreateLiderTramo,
  useUpdateLiderTramo,
  useDeleteLiderTramo,
  useFetchLiderPersonas,
  useCreateLiderPersona,
  useUpdateLiderPersona,
  useDeleteLiderPersona,
  useFetchLiderConfig,
  useUpdateLiderConfig,
} from '@/shared/actions/erp/comisiones/comisiones.actions';
import { useObtenerFiltrosVentas } from '@/shared/actions/erp';
import LoadingDialog from './LoadingDialog';
import type { LiderTramo, LiderPersona, LiderConfig } from '@/lib/commissions/types';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  LIDER_TRAMOS_PREDEFINIDOS,
  LIDER_PERSONAS_PREDEFINIDAS,
  LIDER_CONFIG_PREDEFINIDO,
} from '@/lib/commissions/constants';

interface LiderFormProps {
  onSaved: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`lider-tabpanel-${index}`}
      aria-labelledby={`lider-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function LiderForm({ onSaved }: LiderFormProps) {
  const { data: tramos, isLoading: isLoadingTramos } = useFetchLiderTramos();
  const { data: personas, isLoading: isLoadingPersonas } = useFetchLiderPersonas();
  const { data: config } = useFetchLiderConfig();
  const { data: filtrosData, isLoading: isLoadingFiltros } = useObtenerFiltrosVentas();

  const createTramoMutation = useCreateLiderTramo({ enableSuccessAlert: true });
  const updateTramoMutation = useUpdateLiderTramo({ enableSuccessAlert: true });
  const deleteTramoMutation = useDeleteLiderTramo();

  const createPersonaMutation = useCreateLiderPersona({ enableSuccessAlert: true });
  const updatePersonaMutation = useUpdateLiderPersona({ enableSuccessAlert: true });
  const deletePersonaMutation = useDeleteLiderPersona();

  const updateConfigMutation = useUpdateLiderConfig({ enableSuccessAlert: true });

  const [tabValue, setTabValue] = useState(0);
  const [tramoDialogOpen, setTramoDialogOpen] = useState(false);
  const [personaDialogOpen, setPersonaDialogOpen] = useState(false);
  const [editingTramo, setEditingTramo] = useState<Partial<LiderTramo> | null>(null);
  const [editingPersona, setEditingPersona] = useState<Partial<LiderPersona> | null>(null);
  const [loadingDialogOpen, setLoadingDialogOpen] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [canalSearch, setCanalSearch] = useState('');

  // Estado local para configuración
  const [localConfig, setLocalConfig] = useState<LiderConfig>({
    ...(config?.id ? { id: config.id } : {}),
    bono_por_contrato: (config || LIDER_CONFIG_PREDEFINIDO).bono_por_contrato,
    valor_base_lider: (config || LIDER_CONFIG_PREDEFINIDO).valor_base_lider,
    activo: (config || LIDER_CONFIG_PREDEFINIDO).activo,
  });

  // Obtener lista de canales disponibles
  const canalesDisponibles = useMemo(() => {
    if (!filtrosData?.data) return [];
    try {
      const canales = (filtrosData.data as any).canales || [];
      return canales.filter((canal: any) => canal != null && canal !== '');
    } catch (error) {
      console.error('Error al obtener canales:', error);
      return [];
    }
  }, [filtrosData]);

  // Filtrar canales por búsqueda
  const canalesFiltrados = useMemo(() => {
    if (!canalSearch) return canalesDisponibles;
    return canalesDisponibles.filter((canal: string) =>
      canal.toLowerCase().includes(canalSearch.toLowerCase())
    );
  }, [canalesDisponibles, canalSearch]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handlers para Tramos
  const handleOpenTramoDialog = (tramo?: LiderTramo) => {
    if (tramo) {
      setEditingTramo(tramo);
    } else {
      setEditingTramo({
        desde: 1501,
        hasta: 2500,
        factor: 0.5,
        ventas_fijo: 250,
        capacitacion_fijo: 250,
      });
    }
    setTramoDialogOpen(true);
  };

  const handleCloseTramoDialog = () => {
    setTramoDialogOpen(false);
    setEditingTramo(null);
  };

  const handleSaveTramo = async () => {
    if (!editingTramo) return;

    try {
      if (editingTramo.id) {
        await updateTramoMutation.mutateAsync(editingTramo as LiderTramo);
      } else {
        await createTramoMutation.mutateAsync(editingTramo);
      }
      onSaved();
      handleCloseTramoDialog();
    } catch (error) {
      console.error('Error al guardar tramo:', error);
    }
  };

  const handleDeleteTramo = async (id: number) => {
    if (confirm('¿Estás seguro de eliminar este tramo?')) {
      await deleteTramoMutation.mutateAsync(id);
      onSaved();
    }
  };

  // Handlers para Personas
  const handleOpenPersonaDialog = (persona?: LiderPersona) => {
    if (persona) {
      setEditingPersona(persona);
    } else {
      setEditingPersona({
        nombre: '',
        activo: true,
        canales_venta: [],
      });
    }
    setPersonaDialogOpen(true);
  };

  const handleChangeCanalesPersona = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    const currentCanales = editingPersona?.canales_venta || [];

    if (value.includes('SELECCIONAR_TODOS')) {
      setEditingPersona({
        ...editingPersona,
        canales_venta: currentCanales.length === canalesDisponibles.length ? [] : canalesDisponibles
      });
    } else {
      setEditingPersona({ ...editingPersona, canales_venta: value });
    }
  };

  const handleClosePersonaDialog = () => {
    setPersonaDialogOpen(false);
    setEditingPersona(null);
  };

  const handleSavePersona = async () => {
    if (!editingPersona || !editingPersona.nombre) return;

    try {
      if (editingPersona.id) {
        await updatePersonaMutation.mutateAsync(editingPersona as LiderPersona);
      } else {
        await createPersonaMutation.mutateAsync(editingPersona);
      }
      onSaved();
      handleClosePersonaDialog();
    } catch (error) {
      console.error('Error al guardar persona:', error);
    }
  };

  const handleDeletePersona = async (id: number) => {
    if (confirm('¿Estás seguro de eliminar este líder?')) {
      await deletePersonaMutation.mutateAsync(id);
      onSaved();
    }
  };

  const handleRestablecer = async () => {
    if (!confirm('¿Estás seguro de restablecer todos los parámetros a los valores predefinidos?')) {
      return;
    }

    try {
      setLoadingDialogOpen(true);
      setLoadingProgress(0);

      const totalSteps =
        (tramos?.length || 0) +
        LIDER_TRAMOS_PREDEFINIDOS.length +
        (personas?.length || 0) +
        LIDER_PERSONAS_PREDEFINIDAS.length +
        1;
      let currentStep = 0;

      // Eliminar tramos existentes
      if (tramos && tramos.length > 0) {
        for (const tramo of tramos) {
          if (tramo.id) {
            await deleteTramoMutation.mutateAsync(tramo.id);
            currentStep++;
            setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
          }
        }
      }

      // Crear tramos predefinidos
      for (const tramo of LIDER_TRAMOS_PREDEFINIDOS) {
        await createTramoMutation.mutateAsync(tramo);
        currentStep++;
        setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
      }

      // Eliminar personas existentes
      if (personas && personas.length > 0) {
        for (const persona of personas) {
          if (persona.id) {
            await deletePersonaMutation.mutateAsync(persona.id);
            currentStep++;
            setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
          }
        }
      }

      // Crear personas predefinidas
      for (const nombrePersona of LIDER_PERSONAS_PREDEFINIDAS) {
        await createPersonaMutation.mutateAsync({ nombre: nombrePersona, activo: true });
        currentStep++;
        setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
      }

      // Actualizar config
      await updateConfigMutation.mutateAsync(LIDER_CONFIG_PREDEFINIDO);
      currentStep++;
      setLoadingProgress(Math.round((currentStep / totalSteps) * 100));

      onSaved();
      setLoadingDialogOpen(false);
      setLoadingProgress(0);

      // Actualizar estado local
      setLocalConfig(LIDER_CONFIG_PREDEFINIDO);
    } catch (error) {
      console.error('Error al restablecer:', error);
      setLoadingDialogOpen(false);
      setLoadingProgress(0);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await updateConfigMutation.mutateAsync(localConfig);
      onSaved();
    } catch (error) {
      console.error('Error al guardar configuración:', error);
    }
  };

  if (isLoadingTramos || isLoadingPersonas) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Parámetros de Líder
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RestoreIcon />}
          onClick={handleRestablecer}
          size="small"
          color="warning"
        >
          Restablecer
        </Button>
      </Box>

      {/* Configuración General */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Configuración General
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localConfig.activo}
                      onChange={(e) => setLocalConfig({ ...localConfig, activo: e.target.checked })}
                    />
                  }
                  label="Activo"
                />
                <Button
                  variant="contained"
                  onClick={handleSaveConfig}
                  disabled={updateConfigMutation.isPending}
                  size="small"
                >
                  Guardar Configuración
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />

      {/* Tabs para Tramos y Personas */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Tramos de Comisión" />
          <Tab label="Líderes" />
        </Tabs>
      </Box>

      {/* Panel de Tramos */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Tramos de Comisión
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenTramoDialog()}
            size="small"
          >
            Agregar Tramo
          </Button>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Desde</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Hasta</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Factor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Ventas Fijo</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Capacitación Fijo</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tramos && tramos.length > 0 ? (
                tramos.map((tramo: LiderTramo) => (
                  <TableRow key={tramo.id}>
                    <TableCell>{tramo.desde}</TableCell>
                    <TableCell>{tramo.hasta}</TableCell>
                    <TableCell>{Number(tramo.factor).toFixed(1)}x</TableCell>
                    <TableCell>${Number(tramo.ventas_fijo).toFixed(2)}</TableCell>
                    <TableCell>${Number(tramo.capacitacion_fijo).toFixed(2)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenTramoDialog(tramo)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => tramo.id && handleDeleteTramo(tramo.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No hay tramos configurados
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Panel de Personas */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Líderes
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenPersonaDialog()}
            size="small"
          >
            Agregar Líder
          </Button>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Canales de Venta</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {personas && personas.length > 0 ? (
                personas.map((persona: LiderPersona) => (
                  <TableRow key={persona.id}>
                    <TableCell>{persona.nombre}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {persona.canales_venta && persona.canales_venta.length > 0
                          ? persona.canales_venta.length === canalesDisponibles.length
                            ? 'Todos'
                            : persona.canales_venta.join(', ')
                          : 'Sin asignar'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: persona.activo ? 'success.main' : 'error.main',
                          fontWeight: 500,
                        }}
                      >
                        {persona.activo ? 'Activo' : 'Inactivo'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenPersonaDialog(persona)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => persona.id && handleDeletePersona(persona.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No hay líderes configurados
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Dialog para editar/crear tramo */}
      <Dialog open={tramoDialogOpen} onClose={handleCloseTramoDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTramo?.id ? 'Editar Tramo' : 'Nuevo Tramo'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Desde (contratos)"
              type="number"
              fullWidth
              value={editingTramo?.desde || ''}
              onChange={(e) => setEditingTramo({ ...editingTramo, desde: Number(e.target.value) })}
            />
            <TextField
              label="Hasta (contratos)"
              type="number"
              fullWidth
              value={editingTramo?.hasta || ''}
              onChange={(e) => setEditingTramo({ ...editingTramo, hasta: Number(e.target.value) })}
            />
            <TextField
              label="Factor (ej: 0.5, 1.0)"
              type="number"
              fullWidth
              inputProps={{ step: '0.1' }}
              value={editingTramo?.factor || ''}
              onChange={(e) => setEditingTramo({ ...editingTramo, factor: Number(e.target.value) })}
            />
            <TextField
              label="Ventas Fijo"
              type="number"
              fullWidth
              value={editingTramo?.ventas_fijo || ''}
              onChange={(e) => setEditingTramo({ ...editingTramo, ventas_fijo: Number(e.target.value) })}
            />
            <TextField
              label="Capacitación Fijo"
              type="number"
              fullWidth
              value={editingTramo?.capacitacion_fijo || ''}
              onChange={(e) => setEditingTramo({ ...editingTramo, capacitacion_fijo: Number(e.target.value) })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTramoDialog}>Cancelar</Button>
          <Button
            onClick={handleSaveTramo}
            variant="contained"
            disabled={createTramoMutation.isPending || updateTramoMutation.isPending}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para editar/crear persona */}
      <Dialog open={personaDialogOpen} onClose={handleClosePersonaDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPersona?.id ? 'Editar Líder' : 'Nuevo Líder'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nombre Completo"
              fullWidth
              value={editingPersona?.nombre || ''}
              onChange={(e) => setEditingPersona({ ...editingPersona, nombre: e.target.value })}
            />

            {/* Selector de Canales de Venta */}
            <FormControl fullWidth size="small">
              <InputLabel id="canales-venta-lider-label">Canales de Venta</InputLabel>
              <Select
                labelId="canales-venta-lider-label"
                multiple
                value={editingPersona?.canales_venta || []}
                onChange={handleChangeCanalesPersona}
                input={<OutlinedInput label="Canales de Venta" />}
                disabled={isLoadingFiltros || canalesDisponibles.length === 0}
                renderValue={(selected) =>
                  selected.length === canalesDisponibles.length ? 'Todos' : selected.join(', ')
                }
                error={isLoadingFiltros}
                MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 300 },
                  },
                }}
                onClose={() => setCanalSearch('')}
              >
                <Box sx={{ px: 1.5, py: 0.75, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Buscar canal..."
                    fullWidth
                    value={canalSearch}
                    onChange={(e) => setCanalSearch(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Box>
                <MenuItem value="SELECCIONAR_TODOS" dense>
                  <Checkbox checked={(editingPersona?.canales_venta || []).length === canalesDisponibles.length} size="small" />
                  <ListItemText primary="Seleccionar todos" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </MenuItem>
                {canalesFiltrados.length > 0 ? (
                  canalesFiltrados.map((canal: string) => (
                    <MenuItem key={canal} value={canal} dense>
                      <Checkbox checked={(editingPersona?.canales_venta || []).indexOf(canal) > -1} size="small" />
                      <ListItemText primary={canal} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled dense>
                    <ListItemText primary="No se encontraron canales" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                  </MenuItem>
                )}
              </Select>
              {isLoadingFiltros && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CircularProgress size={12} />
                  Espere hasta que se carguen todos los canales de venta
                </Typography>
              )}
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={editingPersona?.activo ?? true}
                  onChange={(e) => setEditingPersona({ ...editingPersona, activo: e.target.checked })}
                />
              }
              label="Activo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePersonaDialog}>Cancelar</Button>
          <Button
            onClick={handleSavePersona}
            variant="contained"
            disabled={createPersonaMutation.isPending || updatePersonaMutation.isPending}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Dialog */}
      <LoadingDialog
        open={loadingDialogOpen}
        progress={loadingProgress}
        title="Restableciendo parámetros de Líder..."
        message="Eliminando configuración actual y restaurando valores predefinidos."
      />
    </Box>
  );
}
